/**
 * Page Arrêts de principe
 * Route : /jurisprudence/principes
 *
 * Deux modes :
 *  - Browse  : scroll paginé de tous les arrêts is_arret_principe=true
 *  - Recherche : recherche sémantique avec filtre is_arret_principe automatique
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { getDateLocale } from '@/i18n';
import { Loader } from '@/components/Loader';
import {
  PublicHeader, PublicFooter, MCPBadge,
  type PublicFiche,
} from '@/components/jurisprudence';
import { FicheCardPrincipe } from '@/components/jurisprudence/ficheCard';
import { usePublicAuth } from '@/hooks/usePublicAuth';

const SITE_URL    = 'https://smartlawyer.ai';
const OG_IMAGE    = `${SITE_URL}/api/fiches/og-image/default.png`;
const LIMIT       = 20;
const DISPLAY_STEP = 10;

// Valeurs envoyées à l'API (inchangées) ; libellés affichés via DOMAINE_LABEL_KEYS
const DOMAINES = [
  'droit civil', 'droit social', 'droit commercial',
  'droit pénal', 'droit administratif', 'droit fiscal',
];

const DOMAINE_LABEL_KEYS: Record<string, string> = {
  'droit civil':         'principes.domains.civil',
  'droit social':        'principes.domains.social',
  'droit commercial':    'principes.domains.commercial',
  'droit pénal':         'principes.domains.penal',
  'droit administratif': 'principes.domains.administratif',
  'droit fiscal':        'principes.domains.fiscal',
};

export default function PublicPrincipes() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = usePublicAuth();
  const { t } = useTranslation();
  const locale = getDateLocale();
  const domaineLabel = (d: string) => (DOMAINE_LABEL_KEYS[d] ? t(DOMAINE_LABEL_KEYS[d]) : d);

  const [mode, setMode]               = useState<'browse' | 'search'>('browse');
  const [query, setQuery]             = useState(searchParams.get('q') || '');
  const [domaine, setDomaine]         = useState(searchParams.get('domaine') || '');
  const [loading, setLoading]         = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [error, setError]             = useState<string | null>(null);

  // Browse state
  const [browseResults, setBrowseResults] = useState<PublicFiche[]>([]);
  const [browseTotal, setBrowseTotal]     = useState(0);
  const [page, setPage]                   = useState(0);
  const cursorsRef = useRef<(string | null)[]>([null]);

  // Search state
  const [allResults, setAllResults]     = useState<PublicFiche[]>([]);
  const [results, setResults]           = useState<PublicFiche[]>([]);

  // Corpus cross-counts
  const [corpusCounts, setCorpusCounts] = useState<{
    total: number; rgpd_count: number; cjue_count: number;
  } | null>(null);

  useEffect(() => {
    fetch('/api/fiches/corpus-stats')
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setCorpusCounts(d))
      .catch(() => null);
  }, []);
  const [displayCount, setDisplayCount] = useState(0);
  const [searchTime, setSearchTime]     = useState<number | null>(null);

  // ── Browse ──────────────────────────────────────────────────────────────────
  const fetchBrowse = useCallback(async (pageNum: number, dom: string) => {
    setLoading(true);
    setError(null);
    const cursor = pageNum === 0 ? null : (cursorsRef.current[pageNum] ?? null);
    const params = new URLSearchParams({ limit: String(LIMIT), principe_only: 'true' });
    if (cursor) params.set('cursor', cursor);
    if (dom)    params.set('domaine', dom);

    try {
      const res  = await fetch(`/api/fiches/browse-sorted?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setBrowseResults(data.results || []);
      setBrowseTotal(data.total || 0);
      if (data.next_cursor != null) cursorsRef.current[pageNum + 1] = data.next_cursor;
    } catch {
      setError('principes.error_loading');
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, []);

  // ── Recherche sémantique ────────────────────────────────────────────────────
  const doSearch = useCallback(async (q: string, dom: string) => {
    if (!q.trim()) return;
    setLoading(true);
    setError(null);
    setAllResults([]);
    setResults([]);
    setDisplayCount(0);
    const t0 = performance.now();

    try {
      const filters: Record<string, string> = { is_arret_principe: 'true' };
      if (dom) filters.domaine = dom;
      const res = await fetch('/api/fiches/search', {
        method:  'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user?.email ? { 'X-User-Email': user.email, 'X-User-Id': user.uid } : {}),
        },
        body: JSON.stringify({ query: q, limit: 100, filters }),
      });
      const data    = await res.json();
      const fetched: PublicFiche[] = data.results || [];
      setAllResults(fetched);
      setResults(fetched.slice(0, DISPLAY_STEP));
      setDisplayCount(DISPLAY_STEP);
      setSearchTime(Math.round(performance.now() - t0));
    } catch {
      setError('principes.error_search');
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, [user]);

  // ── Init ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const q   = searchParams.get('q') || '';
    const dom = searchParams.get('domaine') || '';
    setQuery(q);
    setDomaine(dom);
    if (q) { setMode('search'); doSearch(q, dom); }
    else   { setMode('browse'); fetchBrowse(0, dom); }
  }, []);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) {
      setMode('browse'); setPage(0); cursorsRef.current = [null];
      setSearchParams(domaine ? { domaine } : {});
      fetchBrowse(0, domaine);
      return;
    }
    setMode('search');
    const params: Record<string, string> = { q: query };
    if (domaine) params.domaine = domaine;
    setSearchParams(params);
    doSearch(query, domaine);
  };

  const handleDomaine = (d: string) => {
    setDomaine(d); setPage(0); cursorsRef.current = [null];
    if (mode === 'search' && query.trim()) doSearch(query, d);
    else fetchBrowse(0, d);
    const params: Record<string, string> = {};
    if (query) params.q = query;
    if (d)     params.domaine = d;
    setSearchParams(params);
  };

  const handleReset = () => {
    setQuery(''); setDomaine(''); setMode('browse'); setPage(0);
    cursorsRef.current = [null];
    setSearchParams({});
    fetchBrowse(0, '');
  };

  const goToPage = (p: number) => {
    if (p > 0 && cursorsRef.current[p] === undefined) return;
    setPage(p);
    fetchBrowse(p, domaine);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const loadMore = () => {
    const next = displayCount + DISPLAY_STEP;
    setResults(allResults.slice(0, next));
    setDisplayCount(next);
  };

  const totalPages    = Math.ceil(browseTotal / LIMIT);
  const hasNextCursor = cursorsRef.current[page + 1] != null;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      <Helmet>
        <title>{t('principes.meta_title')}</title>
        <meta name="description" content={t('principes.meta_description')} />
        <link rel="canonical" href={`${SITE_URL}/jurisprudence/principes`} />
        <meta property="og:title"       content={t('principes.meta_title')} />
        <meta property="og:description" content={t('principes.og_description')} />
        <meta property="og:image"       content={OG_IMAGE} />
        <meta property="og:url"         content={`${SITE_URL}/jurisprudence/principes`} />
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col">
        <PublicHeader />

        <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 py-10">

          {/* Breadcrumb */}
          <nav className="text-xs text-muted-foreground mb-6 flex items-center gap-1.5">
            <Link to="/" className="hover:text-foreground transition-colors">{t('articles.home')}</Link>
            <span className="text-muted-foreground/40">/</span>
            <Link to="/browse" className="hover:text-foreground transition-colors">{t('nav.jurisprudence')}</Link>
            <span className="text-muted-foreground/40">/</span>
            <span className="text-foreground">{t('principes.title')}</span>
          </nav>

          {/* ── En-tête épuré ── */}
          <div className="mb-6">
            <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
              <div className="flex items-center gap-3">
                <span className="text-3xl">⭐</span>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold leading-tight"><Trans t={t} i18nKey="principes.heading" components={{ em: <em className="not-italic text-primary" /> }} /></h1>
                </div>
              </div>
              <div className="flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap">
                <Link to="/jurisprudence/rgpd" className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
                  🔒 {t('reglements.corpus_rgpd')}
                  {corpusCounts && <span className="font-mono text-[10px] opacity-60">{corpusCounts.rgpd_count.toLocaleString(locale)}</span>}
                </Link>
                <span className="text-border">·</span>
                <Link to="/jurisprudence/cjue" className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
                  🇪🇺 {t('reglements.corpus_cjue')}
                  {corpusCounts && <span className="font-mono text-[10px] opacity-60">{corpusCounts.cjue_count.toLocaleString(locale)}</span>}
                </Link>
                <span className="text-border">·</span>
                <Link to="/reglements" className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
                  📋 {t('reglements.breadcrumb')}
                </Link>
              </div>
            </div>

            {/* Stats en ligne */}
            <div className="flex flex-wrap gap-2">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/50">
                <span className="text-base font-bold text-foreground tabular-nums">
                  {initialized
                    ? browseTotal.toLocaleString(locale)
                    : <span className="inline-block w-10 h-4 bg-amber-100 dark:bg-amber-800/40 rounded animate-pulse" />}
                </span>
                <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('principes.stat_decisions')}</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/60 border border-border">
                <span className="text-base font-bold text-foreground">1840+</span>
                <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('principes.stat_years')}</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/60 border border-border">
                <span className="text-base font-bold text-foreground">6</span>
                <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('principes.stat_chambers')}</span>
              </div>
            </div>
          </div>

          {/* ── Zone de recherche unifiée ── */}
          <div className="rounded-2xl border border-border bg-muted/40 px-5 sm:px-6 pt-4 pb-4 mb-6">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <p className="text-[13px] font-bold text-foreground">
                <span className="text-lg leading-none">⭐</span>{' '}
                {initialized
                  ? <Trans t={t} i18nKey="principes.indexed" components={{ strong: <strong className="text-primary">{browseTotal.toLocaleString(locale)}</strong> }} />
                  : t('principes.title')}
              </p>
            </div>
            <div className="h-px bg-border/60 mb-3" />

            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="flex-1 flex items-center gap-3 rounded-xl border border-border bg-background px-4 py-2.5 focus-within:ring-2 focus-within:ring-primary/30 transition-all">
                <svg className="h-4 w-4 text-muted-foreground shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder={t('principes.placeholder')}
                  className="flex-1 bg-transparent outline-none text-[14px] placeholder:text-muted-foreground/60"
                />
                {query && (
                  <button type="button" onClick={handleReset} className="text-muted-foreground hover:text-foreground transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              <button type="submit" disabled={loading}
                className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-[13px] hover:bg-primary/90 disabled:opacity-50 transition-colors shrink-0">
                {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : t('articles.search_submit')}
              </button>
            </form>

            <div className="flex flex-wrap gap-1.5 mt-3">
              <button onClick={() => handleDomaine('')}
                className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                  !domaine ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border text-muted-foreground hover:border-primary/40'
                }`}>
                {t('principes.all_domains')}
              </button>
              {DOMAINES.map(d => (
                <button key={d} onClick={() => handleDomaine(d)}
                  className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                    domaine === d ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border text-muted-foreground hover:border-primary/40'
                  }`}>
                  {domaineLabel(d)}
                </button>
              ))}
            </div>
          </div>

          {/* Compteur */}
          {initialized && !loading && (
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">
                {mode === 'browse' ? (
                  <>
                    <Trans t={t} i18nKey="principes.count" count={browseTotal} components={{ strong: <strong className="text-foreground">{browseTotal.toLocaleString(locale)}</strong> }} />
                    {domaine && <> · <span className="text-foreground">{domaineLabel(domaine)}</span></>}
                    {page > 0 && <span className="ml-2 text-muted-foreground/60">{t('articles.page_suffix', { page: page + 1 })}</span>}
                  </>
                ) : (
                  <>
                    <Trans t={t} i18nKey="articles.results_for" count={allResults.length} components={{
                      strong: <strong className="text-foreground">{allResults.length}</strong>,
                      q: <em className="text-foreground">{t('articles.quoted', { query })}</em>,
                    }} />
                    {searchTime != null && (
                      <span className="ml-2 text-xs text-muted-foreground/60">
                        ({searchTime < 1000 ? `${searchTime} ms` : `${(searchTime / 1000).toFixed(2)} s`})
                      </span>
                    )}
                  </>
                )}
              </p>
              {mode === 'search' && (
                <button onClick={handleReset}
                  className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  {t('principes.see_all')}
                </button>
              )}
            </div>
          )}

          {/* Contenu */}
          {!initialized || (loading && !browseResults.length && !results.length) ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <Loader className="!size-8" />
              <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
            </div>
          ) : error ? (
            <div className="text-center py-24">
              <p className="text-5xl mb-4">⚠️</p>
              <p className="font-semibold mb-2">{t(error)}</p>
              <button onClick={() => mode === 'browse' ? fetchBrowse(page, domaine) : doSearch(query, domaine)}
                className="mt-4 text-sm text-primary underline">{t('common.retry')}</button>
            </div>
          ) : mode === 'browse' ? (
            <>
              {browseResults.length === 0 ? (
                <div className="text-center py-24 text-muted-foreground">
                  <p className="text-5xl mb-4">⭐</p>
                  <p className="font-semibold text-foreground mb-1">
                    {domaine ? t('principes.empty_domain', { domain: domaineLabel(domaine) }) : t('principes.empty')}
                  </p>
                  <p className="text-sm">{t('principes.empty_hint')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {browseResults.map(f => <FicheCardPrincipe key={f.id} fiche={f} />)}
                </div>
              )}

              {(totalPages > 1 || hasNextCursor || page > 0) && (
                <div className="flex items-center justify-center gap-2 mt-10">
                  <button disabled={page === 0 || loading} onClick={() => goToPage(page - 1)}
                    className="px-4 py-2 text-sm rounded-lg border hover:bg-accent disabled:opacity-40 transition-colors">
                    ← {t('common.previous')}
                  </button>
                  <span className="text-sm text-muted-foreground px-3">{t('articles.page', { page: page + 1 })}</span>
                  <button disabled={!hasNextCursor || loading} onClick={() => goToPage(page + 1)}
                    className="px-4 py-2 text-sm rounded-lg border hover:bg-accent disabled:opacity-40 transition-colors">
                    {t('common.next')} →
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              {results.length === 0 ? (
                <div className="text-center py-24 text-muted-foreground">
                  <p className="text-5xl mb-4">🔎</p>
                  <p className="font-semibold text-foreground mb-1">{t('principes.no_results')}</p>
                  <p className="text-sm">{t('principes.no_results_hint')}</p>
                  <button onClick={handleReset} className="mt-4 text-sm text-primary underline">
                    {t('principes.see_all')}
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {results.map(f => <FicheCardPrincipe key={f.id} fiche={f} />)}
                  {displayCount < allResults.length && (
                    <div className="flex justify-center pt-4">
                      <button onClick={loadMore}
                        className="flex items-center gap-2 px-6 py-2.5 rounded-xl border-2 border-border hover:border-primary/40 hover:bg-accent text-sm font-medium transition-all">
                        {t('articles.see_more_remaining', { remaining: allResults.length - displayCount })}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

        </main>

        <PublicFooter />
      </div>

      <MCPBadge />
    </>
  );
}
